/**
 * Demonstrativo: S0 + DRE = S1 (saldo depois). Resultado = SUM(valor) caixa da obra.
 *
 * Select: primeira opção = Período (chave `periodo`); depois os meses (`YYYY-MM`).
 */
import { formatCurrency, formatDate } from '../../../../utils/obraAnalytics'

export const CHAVE_DEMONSTRATIVO_PERIODO = 'periodo'

const chaveDe = (d) => d?.chave || d?.mes_ano

const Linha = ({ label, valor, strong, indent, muted, destaque, nivel = 0 }) => (
    <div className={`flex items-baseline justify-between gap-4 py-1 ${indent || nivel > 0 ? 'pl-4' : ''} ${nivel > 1 ? 'pl-8' : ''} ${strong ? 'pt-2 mt-1 border-t border-gray-100' : ''}`}>
        <span className={`text-sm ${destaque ? 'font-bold text-gray-900' : strong ? 'font-bold text-gray-900' : muted ? 'text-gray-500' : 'text-gray-700'} ${nivel === 1 ? 'text-[13px]' : ''} ${nivel === 2 ? 'text-xs text-gray-500' : ''}`}>
            {label}
        </span>
        <span className={`text-sm tabular-nums whitespace-nowrap ${destaque ? 'font-bold' : strong ? 'font-bold text-gray-900' : muted ? 'text-gray-500' : 'font-medium text-gray-800'} ${destaque && Number(valor) >= 0 ? 'text-emerald-700' : ''} ${destaque && Number(valor) < 0 ? 'text-rose-700' : ''}`}>
            {formatCurrency(valor)}
        </span>
    </div>
)

const DemonstrativoMensal = ({
    demonstrativo,
    demonstrativoPeriodo,
    demonstrativos = [],
    mesSelecionado,
    onChangeMes,
}) => {
    const meses = demonstrativos.length ? demonstrativos : []
    const opcoes = [
        ...(demonstrativoPeriodo ? [demonstrativoPeriodo] : []),
        ...meses,
    ]
    const lista = opcoes.length ? opcoes : (demonstrativo ? [demonstrativo] : [])
    const atual = lista.find((d) => chaveDe(d) === mesSelecionado)
        || demonstrativoPeriodo
        || demonstrativo
        || lista[lista.length - 1]

    if (!atual) {
        return (
            <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
                <p className="text-sm font-bold text-gray-800">Movimentação financeira</p>
                <p className="text-xs text-gray-400 mt-2">Sem movimentação no período selecionado.</p>
            </div>
        )
    }

    // Só exibe macro/grupo com valor (ex.: adiantamento revertido no mesmo recorte soma zero).
    const macros = (atual.macros || atual.plano?.macros || [])
        .map((macro) => ({ ...macro, grupos: (macro.grupos || []).filter((g) => Number(g.total) !== 0) }))
        .filter((macro) => Number(macro.total) !== 0)
    const recebimentos = atual.recebimentos || atual.plano?.recebimentos || { itens: [], total: 0 }
    const itensRecebimentos = (recebimentos.itens || []).filter((i) => Number(i.total) !== 0)
    const saidasPlano = Number(atual.saidas_plano ?? atual.total_geral ?? atual.plano?.total_geral ?? 0)
    const tarifasCtrl = Number(atual.tarifas_ctrl ?? atual.financeiras_ctrl?.total ?? atual.plano?.financeiras_ctrl?.total ?? 0)
    const transferencias = Number(atual.transferencias?.total ?? 0)
    const movimentoConta = Number(
        atual.resultado
        ?? atual.movimento_conta
        ?? atual.variacao_saldo
        ?? (Number(recebimentos.total || 0) + saidasPlano + tarifasCtrl + transferencias)
    )
    const gruposCtrl = (atual.financeiras_ctrl?.grupos || atual.plano?.financeiras_ctrl?.grupos || [])
        .filter((g) => Number(g.total) !== 0)

    return (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-bold text-gray-900">Movimentação financeira</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">Saldo antes + DRE = saldo depois</p>
                </div>
                {lista.length > 1 ? (
                    <select
                        value={chaveDe(atual)}
                        onChange={(e) => onChangeMes?.(e.target.value)}
                        className="text-sm font-semibold text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-rose-200"
                    >
                        {lista.map((d) => (
                            <option key={chaveDe(d)} value={chaveDe(d)}>{d.label}</option>
                        ))}
                    </select>
                ) : (
                    <span className="text-sm font-bold text-rose-800 bg-rose-50 border border-rose-100 px-3 py-1 rounded-full">
                        {atual.label}
                    </span>
                )}
            </div>

            <div className="px-5 py-5 max-w-2xl mx-auto">
                <Linha
                    label={`Saldo em ${formatDate(atual.saldo_inicial?.data)}`}
                    valor={atual.saldo_inicial?.valor}
                    strong
                />

                {Number(recebimentos.total) !== 0 && (
                    <div className="mt-5 mb-1">
                        <p className="text-xs font-bold uppercase tracking-wider mb-2 text-gray-800">Recebimentos</p>
                        {itensRecebimentos.length > 1 && itensRecebimentos.map((item) => (
                            <Linha key={`rec-${item.label}`} label={item.label} valor={item.total} nivel={1} indent />
                        ))}
                        <Linha label="Recebimentos" valor={recebimentos.total} />
                    </div>
                )}

                {macros.map((macro) => (
                    <div key={`${macro.ordem}-${macro.nome}`} className="mt-5 mb-1">
                        <p className="text-xs font-bold uppercase tracking-wider mb-2 text-gray-800">{macro.nome}</p>
                        {(macro.grupos || []).map((grupo) => (
                            <Linha key={`${macro.nome}-${grupo.nome}`} label={grupo.nome} valor={grupo.total} nivel={1} indent />
                        ))}
                    </div>
                ))}

                {Number(tarifasCtrl) !== 0 && (
                    <div className="mt-5 mb-1">
                        <p className="text-xs font-bold uppercase tracking-wider mb-2 text-gray-800">08 / DESPESAS FINANCEIRAS</p>
                        {gruposCtrl.flatMap((grupo) => (grupo.itens || []).filter((i) => Number(i.total) !== 0)).map((item) => (
                            <Linha key={`ctrl-${item.label}`} label={item.label} valor={item.total} nivel={1} indent />
                        ))}
                        <Linha label="Despesas financeiras" valor={tarifasCtrl} />
                    </div>
                )}

                {Number(transferencias) !== 0 && (
                    <div className="mt-5 mb-1">
                        <p className="text-xs font-bold uppercase tracking-wider mb-2 text-gray-800">Transferências</p>
                        <Linha label="Transferências" valor={transferencias} />
                    </div>
                )}

                <div className="mt-4 pt-3 border-t-2 border-gray-200 space-y-1">
                    <Linha label="Resultado (DRE financeiro)" valor={movimentoConta} destaque />
                </div>

                <div className="mt-2 space-y-1">
                    <Linha
                        label={`Saldo em ${formatDate(atual.saldo_final?.data)}`}
                        valor={atual.saldo_final?.valor}
                        strong
                    />
                </div>

                {atual.conferido === false && atual.divergencia_saldo != null && (
                    <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        Identidade S0 + DRE = S1 não fecha: diferença {formatCurrency(atual.divergencia_saldo)}.
                    </p>
                )}
            </div>
        </div>
    )
}

export default DemonstrativoMensal
